<template>
  <div
    v-if="pdfLoading"
    class="d-flex justify-content-center align-items-center"
    style="height: 100%; width: 100vw"
  >
    <img
      v-if="errorMessage === ''"
      src="@/assets/images/common/page-loader.svg"
      alt="Error Icon"
      class="page-loader"
      width="80"
      height="80"
    />
    <div v-else class="error">
      <b-icon-x scale="2" />
      {{ errorMessage }}
    </div>
  </div>
  <div
    v-else
    ref="pdfViewer"
    id="pdf-viewer"
    class="pdfViewer"
    :style="`height: 100%; padding: 8px 0;overflow-x: hidden;`"
    @scroll="documentReadCompleted"
  ></div>
</template>

<script>
import pdfjsLib from 'pdfjs-dist/build/pdf.js'
import pdfService from '@/services/clickWrap'
import 'pdfjs-dist/web/pdf_viewer.css'

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.0.943/pdf.worker.js'

export default {
  name: 'PdfViewer',
  props: ['completedRead', 'docId'],
  data() {
    return {
      PDFProxy: {},
      pdfjsLib: null,
      scale: 1,
      viewer: null,
      canvas: null,
      viewport: null,
      pdfLoading: true,
      errorMessage: ''
    }
  },
  mounted() {
    this.getPdf(this.docId).catch((e) => {
      console.log(e)
      this.$mpTrack('ClickWrap - Failed to load document')
    })
  },
  methods: {
    documentReadCompleted(element) {
      if (element.srcElement) {
        if (
          element.srcElement.scrollTop + element.srcElement.offsetHeight >=
          element.srcElement.scrollHeight - 400
        ) {
          this.$emit('completedReadProgress')
        }
      }
    },
    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    },
    async getDocument(currValue) {
      if (currValue) {
        let base64 = await pdfService.fetchPDFData(currValue)
        return this.blobToBase64(base64.data)
      } else {
        this.$mpTrack('ClickWrap - Document Id not provided')
        this.errorMessage = this.$t('clickWrap.esignDocumentIdMissing')
        return null
      }
    },
    async getPdf(currValue) {
      const data = await this.getDocument(currValue)
      if (data) {
        this.pdfLoading = false
        pdfjsLib
          .getDocument(data)
          .promise.then((pdf) => {
            this.PDFProxy = pdf
            this.viewer = this.$refs.pdfViewer

            for (let page = 1; page <= pdf.numPages; page++) {
              this.canvas = document.createElement('canvas')
              this.canvas.className = 'pdf-page-canvas'
              this.viewer.appendChild(this.canvas)
              this.renderPage(page, this.canvas)
            }
          })
          .catch(() => {
            this.$mpTrack('ClickWrap - Failed to load document', {
              stage: 'paint on canvas'
            })
          })
      } else {
        this.$mpTrack('ClickWrap - Failed to load document', {
          stage: 'failed to convert'
        })
      }
    },
    renderPage(pageNumber, canvas) {
      this.PDFProxy.getPage(pageNumber).then((page) => {
        const viewport = page.getViewport({ scale: 1 })
        let outputScale = 1.8 // window.devicePixelRatio || 2
        if (window.innerWidth < 1100) {
          outputScale = 1.2
        }
        if (window.innerWidth < 600) {
          outputScale = 0.55
        }

        const height = viewport.viewBox[3]
        const width = viewport.viewBox[2]
        canvas.width = Math.floor(width * outputScale)
        canvas.height = Math.floor(height * outputScale)
        canvas.style.width = Math.floor(width * outputScale) + 'px'
        canvas.style.height = Math.floor(height * outputScale) + 'px'
        canvas.style.transform = 'rotateX(0.5turn)'

        const transform =
          outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null

        page.render({
          canvasContext: canvas.getContext('2d'),
          transform: transform,
          viewport: viewport
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
#pdf-viewer {
  overflow: scroll;
  width: 100%;
  background: $background-light;
  scroll-behavior: smooth;
  text-align: center;
  display: grid;
  justify-content: center;
}

.pdf-page-canvas {
  display: block;
  margin: 5px auto;
  border: 1px solid rgba(0, 0, 0, 0.2);
  margin-bottom: 8px;
}
.page-loader {
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}
.error {
  svg {
    color: red;
  }
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
